/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : GithubSWMLogs.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Toolbar,
  AppBar,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LaunchSharpIcon from "@mui/icons-material/LaunchSharp";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { HandleApiActions, labCar } from "../../redux/actions";
import githubIntegrationAction from "../../redux/actions/githubIntegrationAction";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import ruleEngineActions from "../../redux/actions/ruleEngineAction";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import DecisionMatrixPanel from "../legal-requirements/RuleCenter/Executions/DecisionMatrixPanel";
import AttributeUpdateDialog from "./Attributes/AttributeUpdateDialog";

function unixToUTCDate(seconds) {
  if (seconds == null) return null;

  const num = Number(seconds);
  if (!Number.isFinite(num)) return null;

  const ms = num > 1e12 ? num : num * 1000;
  const date = new Date(ms);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
}

function redirectToCommit(payload) {
  const commitUrl = payload?.linked_meta_details?.head_commit?.url;

  if (!commitUrl) return;

  window.open(commitUrl, "_blank", "noopener,noreferrer");
}

const isApproveDisabled = (item, sWModulesList) => {
  let actionType;
  if (Array.isArray(sWModulesList)) {
    const found = sWModulesList.find(
      (m) => String(m?.swm_id) === String(item?.swm_id),
    );
    actionType = found?.action_type;
  } else {
    actionType = sWModulesList?.action_type;
  }
  const actionNum = Number(actionType);
  return actionNum !== 0 && actionNum !== 3;
};

function GithubSWMLogs({
  setGitSWMLogsDetails,
  gitSWMLogsDetails,
  openGitSWMDialog,
  setOpenGitSWMDialog,
  selectedVariantsMany,
  variantOptionList,
  selectedProjectDetails,
  selectedEEComponentDetails,
  handleSWModuleUpdate,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedSWM,
  getSWModulesList,
  selectedSingleVariantDetails,
  checkForStandardWithRuleSet,
  sWModulesList,
}) {
  const [selectedSWModuleUpdateDetails, setSelectedSWModuleUpdateDetails] =
    useState(null);
  const [openSWModuleUpdateDialog, setOpenSWModuleUpdateDialog] =
    useState(false);
  const [isHasTag, setIsHasTag] = useState(false);
  const [linkedMetaId, setLinkedMetaId] = useState(null);

  const columns = [
    { id: "sno", label: "S.No", width: 50 },
    { id: "details", label: "Details", width: 220 },
    { id: "message", label: "Commited Message", width: 220 },
    { id: "by", label: "Commited By", width: 220 },
    { id: "branch", label: "Branch", width: 100 },
    { id: "created", label: "Created At", width: 200 },
    { id: "committed", label: "Commited At", width: 200 },
    { id: "action", label: "Action", width: 100 },
  ];

  const handleClose = () => {
    setOpenGitSWMDialog(false);
    setGitSWMLogsDetails([]);
  };

  const handleClickApprove = (req) => {
    setLinkedMetaId(req?.linked_meta_id);

    const hasTag =
      typeof req?.linked_meta_details?.ref === "string" &&
      req?.linked_meta_details?.ref?.startsWith("refs/tags/");
    setIsHasTag(hasTag);

    setOpenSWModuleUpdateDialog(true);
    setSelectedSWModuleUpdateDetails(req);
    setOpenGitSWMDialog(false);
  };
  return (
    <div>
      <CustomDialogBoxV2
        open={openGitSWMDialog}
        onClose={handleClose}
        title={"GitHub Commit"}
      >
        <Box>
          <TableContainer
            sx={{
              borderRadius: 0,
              boxShadow: 1,
              border: "1px solid #e0e0e0",
              maxHeight: 440,
              scrollbarColor: "#6c6e70ff #f1f1f1",
              overflowX: "auto",
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        color: "#53565A",
                        width: col.width,
                        minWidth: col.width,
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {gitSWMLogsDetails?.length > 0 ? (
                  gitSWMLogsDetails?.map((item, idx) => {
                    const visibility =
                      item?.linked_meta_details?.repository?.visibility || "";
                    const branch =
                      item?.linked_meta_details?.repository?.default_branch;
                    const createdAt = item.created_at
                      ? new Date(item.created_at).toLocaleString()
                      : "";

                    return (
                      <TableRow key={item.id || item.linked_meta_id || `git-log-${idx}`} sx={{ color: "#333" }}>
                        <TableCell
                          sx={{
                            width: columns[0].width,
                            minWidth: columns[0].width,
                          }}
                        >
                          {idx + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: columns[1].width,
                            minWidth: columns[1].width,
                            wordBreak: "break-word",
                          }}
                        >
                          Owner:{" "}
                          {item?.linked_meta_details?.repository?.owner?.name}
                          <br />
                          Visibility: {visibility} <br />
                          Repository:{" "}
                          {item?.linked_meta_details?.repository?.name} <br />
                          Tag/Path: {item?.linked_meta_details?.ref} <br />
                          Type:{" "}
                          {typeof item?.linked_meta_details?.ref === "string" &&
                            item?.linked_meta_details?.ref.startsWith(
                              "refs/tags/",
                            )
                            ? "Tag"
                            : "Branch"}{" "}
                          <br />
                        </TableCell>
                        <TableCell
                          sx={{
                            width: columns[2].width,
                            minWidth: columns[2].width,
                            wordBreak: "break-word",
                          }}
                        >
                          {item?.linked_meta_details?.head_commit?.message}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: columns[3].width,
                            minWidth: columns[3].width,
                          }}
                        >
                          {item?.linked_meta_details?.head_commit?.committer
                            ?.email || "-"}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: columns[4].width,
                            minWidth: columns[4].width,
                          }}
                        >
                          {branch}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: columns[5].width,
                            minWidth: columns[5].width,
                          }}
                        >
                          {createdAt}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: columns[6].width,
                            minWidth: columns[6].width,
                          }}
                        >
                          {unixToUTCDate(
                            item?.linked_meta_details?.repository?.pushed_at,
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: columns[7].width,
                            minWidth: columns[7].width,
                          }}
                        >
                          <Box sx={{ display: "flex" }}>
                            <IconButton
                              onClick={() => handleClickApprove(item)}
                              sx={{ color: "black" }}
                              disabled={isApproveDisabled(item, sWModulesList)}
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => redirectToCommit(item)}
                              sx={{ color: "#515253ff" }}
                            >
                              <LaunchSharpIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography>No Logs Found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CustomDialogBoxV2>
      <VlcSWModuleUpdateDialog
        linkedMetaId={linkedMetaId}
        isHasTag={isHasTag}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleUpdateDialog={openSWModuleUpdateDialog}
        setOpenSWModuleUpdateDialog={setOpenSWModuleUpdateDialog}
        handleSWModuleUpdate={handleSWModuleUpdate}
        selectedSWModuleUpdateDetails={selectedSWModuleUpdateDetails}
        setSelectedSWModuleUpdateDetails={setSelectedSWModuleUpdateDetails}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        selectedSWM={selectedSWM}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        checkForStandardWithRuleSet={checkForStandardWithRuleSet}
      />
    </div>
  );
}

GithubSWMLogs.propTypes = {
  setGitSWMLogsDetails: PropTypes.func,
  gitSWMLogsDetails: PropTypes.array,
  openGitSWMDialog: PropTypes.bool,
  setOpenGitSWMDialog: PropTypes.func,
  selectedVariantsMany: PropTypes.array,
  variantOptionList: PropTypes.array,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  selectedEEComponentDetails: PropTypes.shape({
    variant_ids: PropTypes.array,
  }),
  handleSWModuleUpdate: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  selectedZone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSWM: PropTypes.object,
  getSWModulesList: PropTypes.func,
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  checkForStandardWithRuleSet: PropTypes.func,
  sWModulesList: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      action_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      find: PropTypes.func,
    }),
  ]),
};

export default GithubSWMLogs;

function VlcSWModuleUpdateDialog({
  linkedMetaId,
  isHasTag,
  openSWModuleUpdateDialog,
  setOpenSWModuleUpdateDialog,
  selectedSWModuleUpdateDetails,
  setSelectedSWModuleUpdateDetails,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
}) {
  const navigate = useNavigate();
  const [version, setVersion] = useState("");
  const [oldVersion, setOldVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [showAttributeConfirmation, setShowAttributeConfirmation] =
    useState(false);
  const [openUpdateFlowAttributeDialog, setOpenUpdateFlowAttributeDialog] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [selectedSWM, setSelectedSWM] = useState(null);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);

  const dispatch = useDispatch();

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);

  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);
  const [ruleSetTree, setRuleSetTree] = useState(null);
  const [rulePath, setRulePath] = useState([]);
  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [executionId, setExecutionId] = useState(null);

  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);

  const selectedAttributeComponent = {
    ...selectedSWM,
    ...selectedSWModuleUpdateDetails,
    attributes:
      selectedSWM?.attributes || selectedSWModuleUpdateDetails?.attributes || [],
  };

  const approveGitSWMLog = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      linked_meta_id: Number(linkedMetaId),
    };

    const res = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...githubIntegrationAction.approve_git_swm_logs,
          params: reqObj,
        }),
      ),
    );

    if (res && !res.error) {
      toast.success("Approved");
    }

    return res;
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
  };

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath.length) return;
    const newPath = rulePath
      .slice(0, idx + 1)
      .map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else {
      setRulePath(newPath);
    }
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_set_tree,
            params: { rule_set_id: ruleSetIdToUse },
            show_toast: false,
          }),
        ),
      );
      if (response?.data) {
        setRuleSetTree(response.data);
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setRulePath([{ node: root, answer: null }]);
          setShowRuleSetQuestionDialog(true);
        }
        setShowRuleSetListDialog(false);
      }
    } catch (error) {
      console.error("Failed to fetch rule set questions", error);
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version,
      will_it_impact: false,
      requires_homologation: false,
      reason: discp,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      setDiscp("");

      if (response?.data?.snow_resp) {
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.get_sw_modules,
            params: {
              project_id: Number(selectedSWModuleUpdateDetails?.project_id),
              ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
            },
            show_toast: false,
          }),
        ),
      );
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const isDecisionMatrixContextValid = (data) => {
    if (!data?.standards || data.standards.length === 0) {
      return false;
    }

    return data.standards.some((standard) => {
      if (!standard.rule_sets || standard.rule_sets.length === 0) {
        return false;
      }

      return standard.rule_sets.some(
        (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
      );
    });
  };

  const loadDecisionMatrixContext = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      linked_meta_id: Number(linkedMetaId),
    };
    const res = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...githubIntegrationAction.approve_git_swm_logs,
          params: reqObj,
        }),
      ),
    );
    if (res && !res.error) {
      toast.success("Approved");
    }
    const standardsRes = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.decision_matrix_get_context,
          params: {
            component_type: "SWM",
            component_id: selectedSWModuleUpdateDetails?.swm_id,
            project_id: selectedProjectDetails?.project_id,
            variant_id: selectedSingleVariantDetails?.variant_id,
          },
          show_toast: false,
        }),
      ),
    );
    setOpenSWModuleUpdateDialog(false);
    setShowConfirmation(false);
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);
    setShowDecisionMatrix(true);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_start,
            params: {
              workflowId: payload.workflowId,
              version: payload.version,
            },
            show_toast: false,
          }),
        ),
      );

      setExecutionId(res?.executionId);
      return res;
    }

    if (mode === "COMPLETE") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_node_complete,
            params: {
              executionId: payload.executionId,
              nodeId: payload.nodeId,
              input: payload.input,
            },
            show_toast: false,
          }),
        ),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = () => {};

  const updateComponentAndCheckForDecisionMatrix = async ({
    standardCode,
  } = {}) => {
    try {
      const reCreateTicketParams = {
        details: createTicketData,
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.re_create_ticket_v1,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      if (res && !res.error && standardCode) {
        handleTicketCreationSuccess(standardCode);
      }
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  useEffect(() => {
    if (openSWModuleUpdateDialog && selectedSWModuleUpdateDetails) {
      if (isHasTag) {
        setVersion(
          selectedSWModuleUpdateDetails?.linked_meta_details?.ref?.split(
            "tags/",
          )[1] || "",
        );
        setDiscp(
          selectedSWModuleUpdateDetails?.linked_meta_details?.head_commit
            ?.message || "",
        );
      } else {
        setOldVersion(selectedSWModuleUpdateDetails?.version || "");
        setDiscp(
          selectedSWModuleUpdateDetails?.linked_meta_details?.head_commit
            ?.message || "",
        );
      }
    }
  }, [openSWModuleUpdateDialog, selectedSWModuleUpdateDetails, isHasTag]);

  const clearUpdateSWModuleFields = () => {
    setVersion("");
    setVersionError("");
    setDiscp("");
  };

  const isUpdateSWModuleValid = () => {
    return (
      !versionError &&
      version?.trim() !== selectedSWModuleUpdateDetails?.version &&
      version?.trim()?.length >= 3 &&
      discp?.trim()?.length >= 3
    );
  };

  const handleCloseSWModuleUpdate = () => {
    setOpenSWModuleUpdateDialog(false);
    clearUpdateSWModuleFields();
    setSelectedSWModuleUpdateDetails(null);
    setShowAttributeConfirmation(false);
    setOpenUpdateFlowAttributeDialog(false);
    setShowConfirmation(false);
    setShowIntersectionImpactPrompt(false);
  };

  const handleUpdateClick = () => {
    setShowAttributeConfirmation(true);
  };

  const handleAttributeImpactYes = async () => {
    setShowAttributeConfirmation(false);

    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version,
      will_it_impact: true,
      variant_ids: selectedEEComponentDetails?.variant_ids,
      reason: discp,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (response?.error) {
        return;
      }

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(selectedSWModuleUpdateDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
              swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
              is_dependency_linking: false,
              attribute: true,
            },
            show_toast: false,
          }),
        ),
      );

      await approveGitSWMLog();
      getSWModulesList({ force: true });
      setOpenSWModuleUpdateDialog(false);
      setTimeout(() => {
        setOpenUpdateFlowAttributeDialog(true);
      }, 200);
    } catch (error) {
      console.error("Failed to update attribute impact", error);
    }
  };

  const confirmUpdateAndRedirect = async ({
    selectedSWModuleUpdateDetails,
    willImptIntrOnly = false,
  }) => {
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version,
      will_it_impact: true,
      variant_ids: selectedEEComponentDetails?.variant_ids,
      reason: discp,
    };
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      if (!response?.error) {
        handleCloseSWModuleUpdate();
        if (willImptIntrOnly) {
          await Promise.resolve(
            dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(selectedSWModuleUpdateDetails?.project_id),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
                  swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            ),
          );
        }
        getSWModulesList({ force: true });
        await approveGitSWMLog();
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const updateChangesAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version,
      will_it_impact: false,
      requires_homologation:
        Boolean(rulePath?.at(-1)?.node?.data?.requires_homologation),
      reason: discp,
    };
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      getSWModulesList({ force: true });
      if (!response?.error) {
        handleCloseSWModuleUpdate();
      }
      if (response?.data?.snow_resp) {
        setTicketsDialogOpen(true);
      }

      try {
        await approveGitSWMLog();
      } catch (e) {
        console.error("Git SWM approve failed", e);
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
    setDiscp("");
  };

  const handleTicketCreationSuccess = (standardCode) => {
    setDisabledStandards((prev) => [...new Set([...prev, standardCode])]);
  };

  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "swm",
        entity_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        ...(standard_name == null ? {} : { standard_name }),
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_similar_tickets,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "SW Module",
    });
    setOpenAiPanel(true);
  };

  const handelCloseDecisionMatrix = () => {
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
  };
  return (
    <Box>
      <CustomDialogBoxV2
        open={openSWModuleUpdateDialog}
        onClose={handleCloseSWModuleUpdate}
        title={"Update Software Module"}
        dialogActions={
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <CustomButtonV2
              btnName={"Cancel"}
              size="small"
              type="cancel"
              handleOnClick={handleCloseSWModuleUpdate}
            />
            <CustomButtonV2
              btnName={"Update"}
              size="small"
              type="submit"
              handleOnClick={handleUpdateClick}
              disabled={!isUpdateSWModuleValid()}
            />
          </Box>
        }
      >
        <Stack spacing={2} mb={2} sx={{ width: "350px", height: "150px" }}>
          {Boolean(!isHasTag) && (
            <CustomFormTextFieldV2
              disabled={true}
              size={"small"}
              label={"Old Version"}
              value={oldVersion}
              setValue={setOldVersion}
            />
          )}

          <CustomFormTextFieldV2
            required={true}
            size={"small"}
            label={"New Version"}
            type={"text"}
            value={version}
            setValue={(value) => value.length <= 40 && setVersion(value)}
            maxLength={40}
          />
          <CustomFormTextFieldV2
            required={true}
            size={"small"}
            label={"Reason"}
            value={discp}
            setValue={(value) => value.length <= 100 && setDiscp(value)}
            maxLength={100}
          />
          <CustomDialogBoxV2
            open={showAttributeConfirmation}
            onClose={() => {
              setShowAttributeConfirmation(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <CustomButtonV2
                  btnName="No"
                  size="small"
                  type="cancel"
                  handleOnClick={() => {
                    setShowAttributeConfirmation(false);
                    setTimeout(() => {
                      setShowConfirmation(true);
                    }, 200);
                  }}
                />
                <CustomButtonV2
                  btnName="Yes"
                  size="small"
                  type="submit"
                  handleOnClick={handleAttributeImpactYes}
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: "15px" }}>
                Will it impact the existing attributes?
              </Typography>
            </Box>
          </CustomDialogBoxV2>

          <CustomDialogBoxV2
            open={showConfirmation}
            onClose={() => {
              setShowConfirmation(false);
            }}
            title={"Action"}
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <CustomButtonV2
                  btnName={"No"}
                  size="small"
                  type="cancel"
                  handleOnClick={() => {
                    setShowConfirmation(false);
                    setTimeout(() => {
                      setShowIntersectionImpactPrompt(true);
                    }, 200);
                  }}
                />
                <CustomButtonV2
                  btnName={"Yes"}
                  size="small"
                  type="submit"
                  handleOnClick={() => {
                    confirmUpdateAndRedirect({
                      selectedSWModuleUpdateDetails,
                    });
                  }}
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                }}
              >
                Will it impact the existing dependencies?
              </Typography>
            </Box>
          </CustomDialogBoxV2>

          <CustomDialogBoxV2
            open={showIntersectionImpactPrompt}
            onClose={() => {
              setShowIntersectionImpactPrompt(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <CustomButtonV2
                  size="small"
                  type="cancel"
                  btnName="No"
                  handleOnClick={() => {
                    loadDecisionMatrixContext();
                    updateChangesWithoutStandardAndClose();
                  }}
                />
                <CustomButtonV2
                  size="small"
                  type="submit"
                  btnName="Yes"
                  handleOnClick={() =>
                    confirmUpdateAndRedirect({
                      selectedSWModuleUpdateDetails,
                      willImptIntrOnly: true,
                    })
                  }
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                }}
              >
                Will it impact the existing intersection?
              </Typography>
            </Box>
          </CustomDialogBoxV2>
        </Stack>
      </CustomDialogBoxV2>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openUpdateFlowAttributeDialog}
        setOpenAttributeUpdateDialog={setOpenUpdateFlowAttributeDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenUpdateFlowAttributeDialog(false);
        }}
        onClickSuccess={() => {
          getSWModulesList({ force: true });
          setOpenUpdateFlowAttributeDialog(false);
        }}
        selectedComponent={selectedAttributeComponent}
        componentType={"swm"}
        isAttributeImpactFlow
        variantId={selectedSingleVariantDetails?.variant_id}
        onIntersectionNo={loadDecisionMatrixContext}
        attributeImpactActionType={{ dependency: true }}
        onAttributeImpactComplete={() => {
          getSWModulesList({ force: true });
          setOpenUpdateFlowAttributeDialog(false);
          setSelectedSWM(selectedSWModuleUpdateDetails);
        }}
      />

      <CustomDialogBoxV2
        open={showRuleSetListDialog}
        onClose={handleRuleSetListDialogClose}
        title="Select Rule Set"
        onClickSuccess={async () => {
          await viewRuleSetQuestions(selectedRuleSet);
          setShowRuleSetListDialog(false);
        }}
        onDisabled={!selectedRuleSet}
        onClickReject={handleRuleSetListDialogClose}
        actionButtonStyle={"continuous"}
        successButtonName={"Confirm"}
        rejectButtonName={"Cancel"}
      >
        <Stack spacing={2} sx={{ width: "350px", height: "50px" }}>
          <CustomSelectV2
            size={"small"}
            label={"Select Rule Set"}
            required={true}
            optionsList={
              ruleSetList?.map((rs) => ({
                value: rs?.rule_set_id || rs?.id,
                label: rs?.name,
              })) || []
            }
            selectedValue={selectedRuleSet || " "}
            handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
          />
        </Stack>
      </CustomDialogBoxV2>


      <CustomDialogBoxV2
        open={showRuleSetQuestionDialog}
        onClose={() => {
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        }}
        title={`Rule Set: ${ruleSetTree?.name || ""}`}
        contentSx={{ minHeight: "350px", width: "450px" }}
      >
        <DialogContent>
          <Stack spacing={2} sx={{}}>
            <Stack spacing={1}>
              {rulePath?.map((entry, idx) => {
                const isCategory = entry.node?.type === "category";
                return (
                  <Box
                    key={`rule-entry-${entry?.node?.data?.text || idx}`}
                    sx={{
                      border: "1px solid #e0e0e0",
                      p: 1,
                      borderRadius: 1,
                      backgroundColor:
                        idx === rulePath?.length - 1 ? "#fafafa" : "white",
                    }}
                  >
                    {isCategory ? (
                      <Stack>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Session Ended: {entry.node?.data?.description}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {entry.node?.data?.text}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Box>
                            <CustomButtonV2
                              btnName={"Yes"}
                              handleOnClick={() => handleAnswerAt(idx, "yes")}
                              type={entry.answer === "yes" ? "submit" : "cancel"}
                            />
                          </Box>
                          <Box>
                            <CustomButtonV2
                              btnName={"No"}
                              handleOnClick={() => handleAnswerAt(idx, "no")}
                              type={entry.answer === "no" ? "submit" : "cancel"}
                            />
                          </Box>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                );
              })}

              {rulePath?.at(-1)?.node?.type === "category" ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    mt={2}
                  >
                    <CustomButtonV2
                      type="submit"
                      btnName={"Finish"}
                      handleOnClick={() => {
                        updateChangesAndClose();
                        setShowRuleSetQuestionDialog(false);
                        setRuleSetTree(null);
                        setRulePath([]);
                        setSelectedRuleSet("");
                        handleCloseSWModuleUpdate();
                      }}
                    />
                  </Stack>
                ) : null
              }
            </Stack>
          </Stack>
        </DialogContent>
      </CustomDialogBoxV2>


      <Box>


        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets?tab=${1}`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>

        <Dialog
          open={showDecisionMatrix}
          onClose={handelCloseDecisionMatrix}
          fullScreen
          PaperProps={{
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          }}
        >
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            <DecisionMatrixPanel
              context={decisionMatrixContext}
              onExecuteWorkflow={handleExecuteWorkflow}
              onViewExecution={handleViewExecution}
              onClose={handelCloseDecisionMatrix}
              onDecisionMatrixAction={updateComponentAndCheckForDecisionMatrix}
              handelGetSimilerTickets={handelGetSimilerTickets}
              similarTickets={similarTickets}
              setSimilarTickets={setSimilarTickets}
              entityDetails={selectedEntity}
              handelGetAiSuggestion={handelGetAiSuggestion}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              openAiPanel={openAiPanel}
              setOpenAiPanel={setOpenAiPanel}
              setCreateTicketData={setCreateTicketData}
              disabledStandards={disabledStandards}
            />
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}

VlcSWModuleUpdateDialog.propTypes = {
  linkedMetaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isHasTag: PropTypes.bool,
  openSWModuleUpdateDialog: PropTypes.bool,
  setOpenSWModuleUpdateDialog: PropTypes.func,
  selectedSWModuleUpdateDetails: PropTypes.shape({
    swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    version: PropTypes.string,
    attributes: PropTypes.array,
    linked_meta_details: PropTypes.shape({
      head_commit: PropTypes.shape({
        message: PropTypes.string,
      }),
      ref: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ split: PropTypes.func }),
      ]),
    }),
  }),
  setSelectedSWModuleUpdateDetails: PropTypes.func,
  selectedEEComponentDetails: PropTypes.shape({
    variant_ids: PropTypes.array,
  }),
  getSWModulesList: PropTypes.func,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};
